import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Alert,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorOutlineIcon,
  ReportProblem as ReportProblemIcon,
} from '@mui/icons-material';

const EoyTooltipContent = ({ breakdown, formatCurrency, eoyCapex, eoyOpex, eoyTotal, ppmEoyTotal }) => (
  <Box sx={{ minWidth: 220, py: 0.5 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
      EOY Projection
    </Typography>
    {(eoyCapex > 0 || eoyOpex > 0) && (
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.85 }}>
        C: {formatCurrency(eoyCapex)} | O: {formatCurrency(eoyOpex)}
      </Typography>
    )}
    {ppmEoyTotal > 0 && ppmEoyTotal !== eoyTotal && (
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.75, fontStyle: 'italic' }}>
        PPM projection: {formatCurrency(ppmEoyTotal)}
      </Typography>
    )}
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.25, opacity: 0.9 }}>
      YTD Actuals (through {breakdown.cutoffLabel})
    </Typography>
    {breakdown.ytdActuals.map(({ month, amount }) => (
      <Box key={month} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>{month}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(amount)}</Typography>
      </Box>
    ))}
    {breakdown.caForecastRemaining.length > 0 && (
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5, mt: 0.5 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.25, opacity: 0.9 }}>
          Official Forecast ({breakdown.caForecastRemaining[0]?.month}–Dec)
          {breakdown.hasOfficial ? ' ← official upload' : ' ← staffing model'}
        </Typography>
        {breakdown.caForecastRemaining.map(({ month, amount }) => (
          <Box key={month} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>{month}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(amount)}</Typography>
          </Box>
        ))}
      </Box>
    )}
    {(breakdown.ppmRemaining.companies.length > 0 || breakdown.ppmRemaining.fixedCosts.length > 0) && (
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5, mt: 0.5 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.25, opacity: 0.9 }}>
          PPM Estimate — non-CA + Fixed
        </Typography>
        {breakdown.ppmRemaining.companies.map(({ name, monthlyAmount }) => (
          <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>{name}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(monthlyAmount)}/mo</Typography>
          </Box>
        ))}
        {breakdown.ppmRemaining.fixedCosts.map(({ name, monthlyAmount }) => (
          <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>{name} (fixed)</Typography>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(monthlyAmount)}/mo</Typography>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

const STATUS_CHIP = {
  'on-track':        { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  'at-risk':         { color: 'warning', icon: <WarningAmberIcon fontSize="small" /> },
  'needs-attention': { color: 'error',   icon: <ReportProblemIcon fontSize="small" /> },
  'off-track':       { color: 'error',   icon: <ErrorOutlineIcon fontSize="small" /> },
};
const STATUS_DEFINITIONS = {
  'on-track':        'On Track — Projected forecast is within approved budget.',
  'at-risk':         'At Risk — YTD actuals exceed YTD projection, but full-year forecast is within budget.',
  'needs-attention': 'Needs Attention — YTD actuals exceed YTD projection AND full-year forecast exceeds approved budget.',
  'off-track':       'Off Track — Projected forecast exceeds approved budget.',
};

const BudgetSummary = ({
  formData,
  outcomeData,
  calculateCapexOpexTotals,
  approvedBudgetComparison,
  formatCurrency,
  eoyTotal = 0,
  ppmEoyTotal = 0,
  eoyBreakdown = null,
  eoyCapex = 0,
  eoyOpex = 0,
  spendStatus = null,
}) => {
  const { totalCapex, totalOpex } = calculateCapexOpexTotals;
  const totalStaffed = totalCapex + totalOpex;

  // Variance: Approved - EOY (when EOY exists), else Approved - Staffed
  const varianceBase = eoyTotal > 0 ? eoyTotal : totalStaffed;
  const variance = approvedBudgetComparison
    ? approvedBudgetComparison.totalApproved - varianceBase
    : null;
  const isOverBudget = variance !== null && variance < 0;

  const hasApproved = !!approvedBudgetComparison;
  // How many cards are visible — drives responsive md breakpoint so all cards fill one row
  const cardCount = 2 + (hasApproved ? 1 : 0) + (eoyTotal > 0 ? 1 : 0);
  const cardMd = ({ 2: 6, 3: 4, 4: 3 })[cardCount] || 3;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h6">
            Budget Summary
          </Typography>
        </Box>

        <Grid container spacing={2} alignItems="stretch">
          {/* 1. Approved Budget */}
          {hasApproved ? (
            <Grid item xs={12} md={cardMd} sx={{ display: 'flex' }}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: alpha('#4caf50', 0.1), height: '100%', width: '100%', boxSizing: 'border-box' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Approved Budget
                </Typography>
                <Typography variant="h5" color="success.main" fontWeight={600}>
                  {formatCurrency(approvedBudgetComparison.totalApproved)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  CAPEX: {formatCurrency(approvedBudgetComparison.approvedCapex)} | OPEX: {formatCurrency(approvedBudgetComparison.approvedOpex)}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip label="Baseline" size="small" color="success" variant="outlined" />
                </Box>
              </Card>
            </Grid>
          ) : (
            <Grid item xs={12} md={cardMd} sx={{ display: 'flex' }}>
              <Alert severity="info" sx={{ height: '100%', width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    No Approved Budget Set
                  </Typography>
                  <Typography variant="caption">
                    Set an approved budget in the outcome details to see variance analysis
                  </Typography>
                </Box>
              </Alert>
            </Grid>
          )}

          {/* 2. Staffed Budget */}
          <Grid item xs={12} md={cardMd} sx={{ display: 'flex' }}>
            <Card variant="outlined" sx={{ p: 2, bgcolor: alpha('#2196f3', 0.1), height: '100%', width: '100%', boxSizing: 'border-box' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Staffed Budget
              </Typography>
              <Typography variant="h5" color="primary.main" fontWeight={600}>
                {formatCurrency(totalStaffed)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                CAPEX: {formatCurrency(totalCapex)} | OPEX: {formatCurrency(totalOpex)}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                <Chip
                  label={`${formData.milestones?.length || 0} milestones`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${formData.milestones?.reduce((sum, m) => sum + (m.roles?.length || 0), 0) || 0} roles`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Card>
          </Grid>

          {/* 3. EOY Projection */}
          {eoyTotal > 0 && (
            <Grid item xs={12} md={cardMd} sx={{ display: 'flex' }}>
              <Tooltip
                arrow
                title={eoyBreakdown
                  ? <EoyTooltipContent breakdown={eoyBreakdown} formatCurrency={formatCurrency} eoyCapex={eoyCapex} eoyOpex={eoyOpex} eoyTotal={eoyTotal} ppmEoyTotal={ppmEoyTotal} />
                  : 'Full-year landing estimate: actuals through the latest uploaded month, projected for remaining months.'
                }
              >
                <Card variant="outlined" sx={{ p: 2, bgcolor: alpha('#0288d1', 0.1), cursor: 'help', height: '100%', width: '100%', boxSizing: 'border-box' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    EOY Projection
                  </Typography>
                  <Typography variant="h5" color="info.main" fontWeight={600}>
                    {formatCurrency(eoyTotal)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {ppmEoyTotal > 0 && ppmEoyTotal !== eoyTotal
                      ? 'Official forecast + actuals'
                      : 'Actuals + projected remainder'}
                  </Typography>
                  {ppmEoyTotal > 0 && ppmEoyTotal !== eoyTotal && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2, fontSize: '0.7rem', fontStyle: 'italic' }}>
                      PPM: {formatCurrency(ppmEoyTotal)}
                    </Typography>
                  )}
                  {(eoyCapex > 0 || eoyOpex > 0) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      C: {formatCurrency(eoyCapex)} | O: {formatCurrency(eoyOpex)}
                    </Typography>
                  )}
                </Card>
              </Tooltip>
            </Grid>
          )}

          {/* 4. Variance: Approved − EOY (or Staffed when no EOY); status chip replaces budget badge when actuals exist */}
          {hasApproved && (
            <Grid item xs={12} md={cardMd} sx={{ display: 'flex' }}>
              <Tooltip
                arrow
                title={spendStatus ? (STATUS_DEFINITIONS[spendStatus.status] || spendStatus.text) : ''}
                placement="top"
                componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}
                disableHoverListener={!spendStatus}
              >
                <Card variant="outlined" sx={{
                  p: 2,
                  bgcolor: isOverBudget ? alpha('#f44336', 0.1) : alpha('#4caf50', 0.1),
                  height: '100%',
                  width: '100%',
                  boxSizing: 'border-box',
                  cursor: spendStatus ? 'help' : 'default',
                }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Variance
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {variance > 0 ? (
                      <TrendingDownIcon color="success" />
                    ) : variance < 0 ? (
                      <TrendingUpIcon color="error" />
                    ) : (
                      <CheckCircleIcon color="success" />
                    )}
                    <Typography
                      variant="h5"
                      color={isOverBudget ? 'error.main' : 'success.main'}
                      fontWeight={600}
                    >
                      {formatCurrency(Math.abs(variance))}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    vs {eoyTotal > 0 ? 'EOY projection' : 'staffed budget'}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {spendStatus ? (
                      <Chip
                        icon={STATUS_CHIP[spendStatus.status]?.icon}
                        label={spendStatus.text}
                        size="small"
                        color={STATUS_CHIP[spendStatus.status]?.color || 'default'}
                        variant="filled"
                      />
                    ) : (
                      <Chip
                        label={variance > 0 ? 'Under Budget' : variance < 0 ? 'Over Budget' : 'On Budget'}
                        size="small"
                        color={isOverBudget ? 'error' : 'success'}
                        variant="filled"
                      />
                    )}
                  </Box>
                </Card>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        {/* Fixed Costs Summary */}
        {formData.fixedCosts && formData.fixedCosts.length > 0 && (
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Fixed Costs Included
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {formData.fixedCosts.map((cost, index) => {
                const startDate = new Date(cost.start_date + '-01');
                const endDate = new Date(cost.end_date + '-01');
                const duration = Math.max(1, Math.round((endDate - startDate) / (1000 * 60 * 60 * 24 * 30)) + 1);
                const totalCost = (cost.monthlyCost || 0) * duration;

                return (
                  <Tooltip
                    key={cost.id || index}
                    title={`${cost.type}: ${formatCurrency(totalCost)} (${duration} months)`}
                    arrow
                  >
                    <Chip
                      label={`${cost.type}: ${formatCurrency(totalCost)}`}
                      size="small"
                      variant="outlined"
                      color={cost.capex_classification === 'CAPEX' ? 'primary' : 'default'}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BudgetSummary;
